import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { identifyUser, resetUser } from "@/lib/posthog";
import { useNavigate } from "react-router-dom";

export interface SubscriptionInfo {
  subscribed: boolean;
  product_id: string | null;
  subscription_end: string | null;
  status: string | null;
}

interface AuthContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  subscription: SubscriptionInfo;
  subscriptionLoading: boolean;
  refreshSubscription: (options?: { force?: boolean }) => Promise<void>;
  signOut: () => Promise<void>;
}

const defaultSubscription: SubscriptionInfo = {
  subscribed: false,
  product_id: null,
  subscription_end: null,
  status: null,
};

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  loading: true,
  subscription: defaultSubscription,
  subscriptionLoading: true,
  refreshSubscription: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<SubscriptionInfo>(defaultSubscription);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  const checkSubscription = useCallback(async (options?: { force?: boolean }) => {
    setSubscriptionLoading(true);
    try {
      const { data: { session: currentSession } } = await supabase.auth.getSession();
      if (!currentSession) {
        setSubscription(defaultSubscription);
        setSubscriptionLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke("check-subscription", {
        body: options?.force ? { force: true } : undefined,
      });
      if (error) {
        console.error("check-subscription error:", error);
        setSubscription(defaultSubscription);
      } else {
        setSubscription(data as SubscriptionInfo);
      }
    } catch (err) {
      console.error("check-subscription failed:", err);
      setSubscription(defaultSubscription);
    } finally {
      setSubscriptionLoading(false);
    }
  }, []);

  useEffect(() => {
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setSession(session);
        setLoading(false);
        if (session) {
          identifyUser(session.user.id, { email: session.user.email });
          setTimeout(() => checkSubscription(), 0);
        } else {
          resetUser();
          setSubscription(defaultSubscription);
          setSubscriptionLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        // Valider que l'utilisateur existe toujours côté serveur
        const { error } = await supabase.auth.getUser();
        if (error) {
          console.warn("Session locale invalide, déconnexion automatique:", error.message);
          await supabase.auth.signOut();
          setSession(null);
          setLoading(false);
          setSubscriptionLoading(false);
          return;
        }
        setSession(session);
        setLoading(false);
        identifyUser(session.user.id, { email: session.user.email });
        // checkSubscription() est déjà déclenché par onAuthStateChange
        // (INITIAL_SESSION) ; on évite ainsi un double appel coûteux au boot.
      } else {
        setSession(null);
        setLoading(false);
        setSubscriptionLoading(false);
      }
    });

    return () => authSub.unsubscribe();
  }, [checkSubscription]);

  // Auto-refresh toutes les 5 min (au lieu de 60s) : check-subscription appelle
  // Stripe côté serveur, un poll par minute et par onglet était trop coûteux.
  useEffect(() => {
    if (!session) return;
    const interval = setInterval(checkSubscription, 5 * 60_000);
    return () => clearInterval(interval);
  }, [session, checkSubscription]);

  const signOut = async () => {
    resetUser();
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user: session?.user ?? null,
        loading,
        subscription,
        subscriptionLoading,
        refreshSubscription: checkSubscription,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function RequireAuth({ children }: { children: ReactNode }) {
  const { session, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !session) {
      navigate("/login");
    }
  }, [session, loading, navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  if (!session) return null;

  return <>{children}</>;
}
